import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { arrayMove } from "@dnd-kit/sortable";

import { useUpdateClaimDraftMutation } from "../../../../store/Company/companyApiSlice";
import { CommonToast } from "../../../common/Toast";
import { processIndustryData } from "../../../../utils/Helper";

/* The Industries section's behaviour. */

/* Transcribed verbatim — this is the branch that decides which endpoint the reorder is written to. */
export const industryUpdateUrl = (company) =>
  company?.companyID?.startsWith("TRX")
    ? `sub-industry/update/${company.companyID}`
    : `claimdraft/${company?.draftID}/update_sub_industry`;

/* Transcribed: dropping a row renumbers every row's `sequence` from 1, which
   is what the backend orders by. Pure, so the reorder is testable without a
   drag. */
export const reorderIndustries = (rows, activeId, overId) => {
  if (!overId || activeId === overId) return rows;

  const from = rows.findIndex((r) => r.id === activeId);
  const to = rows.findIndex((r) => r.id === overId);
  /* A row that is not in the list cannot be moved — arrayMove would happily
     take -1 and corrupt the order. */
  if (from === -1 || to === -1) return rows;

  return arrayMove(rows, from, to).map((row, index) => ({
    ...row,
    sequence: index + 1,
  }));
};

/* The rows the section lists. */
export const industryRows = (company) => {
  const saved = company?.sub_industry_data;
  if (Array.isArray(saved) && saved.length > 0) return saved;

  const src = company?.industry;
  if (!src) return [];

  const derive = (row, index) => {
    const name = row?.sub_industry || row?.industry;
    if (!name || typeof name !== "string") return null;
    return {
      id: `industry-${row?.id ?? index}`,
      sub_industry: name,
      sequence: index + 1,
      derived: true,
    };
  };

  /* A list of industry rows. */
  if (Array.isArray(src)) return src.map(derive).filter(Boolean);

  /* One row carrying a sub-industry LIST. */
  if (Array.isArray(src.sub_industry)) {
    return src.sub_industry
      .map((row, i) => derive(typeof row === "string" ? { id: `${src.id}-${i}`, sub_industry: row } : row, i))
      .filter(Boolean);
  }

  /* One row whose sub-industry is a single name — TRXIN0000033's shape. */
  const only = derive(src, 0);
  return only ? [only] : [];
};

export const useIndustrySection = ({ onSaved }) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);

  const [rows, setRows] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [updateClaimDraft] = useUpdateClaimDraftMutation();

  useEffect(() => {
    /* Transcribed, with two corrections: the live code assigns `sub_industry_data` straight through, so a record without one leaves `users` undefined and the very... */
    setRows(industryRows(currentEditingCompany));
  }, [currentEditingCompany]);

  const handleDragEnd = (event) => {
    const { active, over } = event || {};
    setRows((current) => reorderIndustries(current, active?.id, over?.id));
  };

  const save = async () => {
    /* Derived rows are read off the `industry` record and carry ITS id, which
       is not a SubIndustry id — saving them would write the wrong reference.
       There is also nothing to reorder, so Continue just moves on. */
    if (rows.length > 0 && rows.every((row) => row.derived)) {
      onSaved?.();
      return;
    }

    setIsSaving(true);
    try {
      const res = await updateClaimDraft({
        token,
        url: industryUpdateUrl(currentEditingCompany),
        data: processIndustryData(rows),
      });

      /* Transcribed exactly, including reading `res.error.message`. */
      if (!res.error) {
        CommonToast.success("Industry updated successfully");
        onSaved?.();
      } else {
        CommonToast.error(res.error.message || "Failed to update sequence");
      }
    } catch (error) {
      CommonToast.error(error.message || "Failed to update sub-industry");
    } finally {
      setIsSaving(false);
    }
  };

  return {
    rows,
    handleDragEnd,
    save,
    isSaving,
    /* Transcribed: the glyph beside the heading reads the industry record's
       own status, not the company's. */
    displayStatus: currentEditingCompany?.industry?.status,
  };
};

export default useIndustrySection;
